import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Building2, Save, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, formatPartnerError, canPartnerAdmin } from "./partnerApi";
import "./PartnerPortal.css";

export default function PartnerProfile() {
  const { me, reloadMe } = useOutletContext();
  const org = me.partner_org;
  const [form, setForm] = useState({
    contact_name: org.contact_name || "",
    contact_email: org.contact_email || "",
    website: org.website || "",
    notes: org.notes || "",
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [pwdBusy, setPwdBusy] = useState(false);
  const canEdit = canPartnerAdmin(me);

  const save = async () => {
    setBusy(true);
    try {
      await partnerApi.patch("/partner/portal/profile", form);
      toast.success("Profile updated");
      reloadMe();
    } catch (e) {
      toast.error(formatPartnerError(e));
    } finally { setBusy(false); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords don't match"); return; }
    if (pwd.new_password.length < 12) { toast.error("Password must be ≥12 characters"); return; }
    setPwdBusy(true);
    try {
      await partnerApi.post("/partner/portal/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatPartnerError(e));
    } finally { setPwdBusy(false); }
  };

  return (
    <div data-testid="pp-profile">
      <h1 className="hw-pp__page-title">
        <Building2 className="w-5 h-5"
                    style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        Organization profile
      </h1>
      <p className="hw-pp__page-sub">
        These details are visible to HarborWell staff and appear on your partner integration records.
        {!canEdit && " Editing requires OWNER or ADMIN role."}
      </p>

      <div className="hw-pp__panel">
        <h3>Contact information</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem" }}>
          <label className="hw-pp__field">
            <span className="hw-pp__field-label">Contact name</span>
            <input className="hw-pp__input" value={form.contact_name}
                    disabled={!canEdit}
                    onChange={(e) => setForm({...form, contact_name: e.target.value})}
                    data-testid="pp-profile-contact-name" />
          </label>
          <label className="hw-pp__field">
            <span className="hw-pp__field-label">Contact email</span>
            <input className="hw-pp__input" type="email" value={form.contact_email}
                    disabled={!canEdit}
                    onChange={(e) => setForm({...form, contact_email: e.target.value})}
                    data-testid="pp-profile-contact-email" />
          </label>
          <label className="hw-pp__field">
            <span className="hw-pp__field-label">Website</span>
            <input className="hw-pp__input" value={form.website}
                    disabled={!canEdit}
                    onChange={(e) => setForm({...form, website: e.target.value})}
                    data-testid="pp-profile-website" />
          </label>
          <label className="hw-pp__field" style={{ gridColumn: "1 / -1" }}>
            <span className="hw-pp__field-label">Notes</span>
            <textarea className="hw-pp__input" rows={3} value={form.notes}
                        disabled={!canEdit}
                        onChange={(e) => setForm({...form, notes: e.target.value})} />
          </label>
        </div>
        {canEdit && (
          <button className="hw-pp__btn hw-pp__btn--primary" onClick={save}
                    disabled={busy} data-testid="pp-profile-save"
                    style={{ marginTop: "0.75rem" }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save changes
          </button>
        )}
      </div>

      <div className="hw-pp__panel">
        <h3>
          <span><Lock className="w-4 h-4"
                       style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.3rem" }} />
            Change password</span>
          <small>Your personal password — not shared with the organization</small>
        </h3>
        <form onSubmit={changePwd} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.7rem" }}>
          <label className="hw-pp__field">
            <span className="hw-pp__field-label">Current password</span>
            <input className="hw-pp__input" type="password" required
                    value={pwd.current_password}
                    onChange={(e) => setPwd({...pwd, current_password: e.target.value})}
                    data-testid="pp-pwd-current" />
          </label>
          <label className="hw-pp__field">
            <span className="hw-pp__field-label">New password (≥12 chars)</span>
            <input className="hw-pp__input" type="password" required minLength={12}
                    value={pwd.new_password}
                    onChange={(e) => setPwd({...pwd, new_password: e.target.value})}
                    data-testid="pp-pwd-new" />
          </label>
          <label className="hw-pp__field">
            <span className="hw-pp__field-label">Confirm</span>
            <input className="hw-pp__input" type="password" required
                    value={pwd.confirm}
                    onChange={(e) => setPwd({...pwd, confirm: e.target.value})}
                    data-testid="pp-pwd-confirm" />
          </label>
          <div style={{ gridColumn: "1 / -1" }}>
            <button type="submit" className="hw-pp__btn hw-pp__btn--primary"
                      disabled={pwdBusy || !pwd.current_password || pwd.new_password.length < 12
                                  || pwd.new_password !== pwd.confirm}
                      data-testid="pp-pwd-submit">
              {pwdBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
              Change password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
